/* eslint-disable camelcase */
/* eslint-disable func-style */
import React, { useCallback, useMemo } from 'react';
import { Button, Confirm, Card, Icon, Label } from 'semantic-ui-react';
import { useMonitoringContext } from './MonitorContext.jsx';
import useToggle from '../hooks/useToggle.jsx';
import useTimeout from '../hooks/useTimeout.jsx';
import { useSubmit, useSubscriber } from './hooks.js';

export default function MonitorView() {
  const {
    state: { isMonitoring, isExpired },
  } = useMonitoringContext();

  if (isMonitoring || isExpired) {
    return <MonitoredData />;
  }

  return (
    <React.Fragment>
      <NodesToMonitorDetails />
      <StartMonitoringButton />
    </React.Fragment>
  );
}

function NodesToMonitorDetails() {
  const {
    state: { nodesToMonitor, defaultExpiresIn },
  } = useMonitoringContext();

  const nodeNames = useMemo(() => {
    const [hostname, ...remaining] = nodesToMonitor.map((n) => n.hostname);

    return remaining.length == 0
      ? hostname
      : `${remaining.join(', ')} and ${hostname}`;
  }, [nodesToMonitor]);

  return (
    <React.Fragment>
      <p>
        You are about do start the monitor for the node
        {nodesToMonitor.length == 1 ? '' : 's'} <b>{nodeNames}</b>.
      </p>
      <p>
        Once you start monitoring, this page shall expire within{' '}
        {defaultExpiresIn}.
      </p>
    </React.Fragment>
  );
}

function StartMonitoringButton() {
  const {
    state: { triggerMonitoringUrl, defaultExpiresIn, message },
    dispatch,
    actions,
  } = useMonitoringContext();
  const hasError = message && message.error;

  const {
    value: isConfirmationOpen,
    setFalse: closeConfirmation,
    setTrue: openConfirmation,
  } = useToggle(false);

  const onError = useCallback((e) => {
    closeConfirmation();
    dispatch({ type: actions.ON_MONITOR_ERROR, payload: e });
  }, []);

  const onSubmit = useCallback((res) => {
    closeConfirmation();
    dispatch({ type: actions.MONITOR_NODES, payload: res });
  }, []);

  const handleSubmit = useSubmit(triggerMonitoringUrl, onSubmit, onError);

  return (
    <React.Fragment>
      <Confirm
        content={`Once you start monitoring, this page shall expire within ${defaultExpiresIn}.`}
        header='Are you sure you want to start monitoring?'
        open={isConfirmationOpen}
        onClose={closeConfirmation}
        onConfirm={handleSubmit}
      />

      <Button
        fluid
        primary
        disabled={hasError}
        size='massive'
        onClick={openConfirmation}
      >
        Start Monitoring
      </Button>
    </React.Fragment>
  );
}

function MonitoredData() {
  const {
    state: { nodesToMonitor, expiresAt },
    dispatch,
    actions,
  } = useMonitoringContext();

  const expireDelay = useMemo(
    () => (expiresAt ? new Date(expiresAt) - Date.now() : null),
    [expiresAt],
  );

  useTimeout(
    () => {
      dispatch({ type: actions.EXPIRE_MONITORING });
    },
    expiresAt ? expireDelay : null,
  );

  return (
    <div className='edgeCards'>
      <Card.Group>
        {nodesToMonitor.map((node) => (
          <Card
            key={node.hostname}
            fluid
          >
            <Card.Content>
              <Card.Header>{node.hostname}</Card.Header>
              <MonitoredNode node={node} />
            </Card.Content>
          </Card>
        ))}
      </Card.Group>
    </div>
  );
}

function MonitoredNode({ node: { hostname, ...initialStatus } }) {
  const {
    installation_status_message,
    installation_status,
    installation_status_updated_at,
  } = useSubscriber(`/models/node/edge_update/${hostname}`, initialStatus);

  const updatedAtFormatted = useMemo(
    // eslint-disable-next-line no-use-before-define
    () => getFormattedUpdateAt(installation_status_updated_at),
    [installation_status_updated_at],
  );

  return (
    <React.Fragment>
      <Card.Meta>
        <a>
          <Icon name='clock' />
          <strong>Last update:</strong>
          <br />
          {updatedAtFormatted}
        </a>
      </Card.Meta>
      <Card.Description>
        <p className='edgeDescription'>{installation_status_message}</p>

        <StatusLabel status={installation_status} />
      </Card.Description>
    </React.Fragment>
  );
}

function StatusLabel({ status }) {
  // eslint-disable-next-line no-use-before-define
  const [label, color] = getInstallationStatusLabelAndColor(status);

  return (
    <Label
      className='edgeLabel'
      color={color}
      size='big'
    >
      {label}
    </Label>
  );
}

function getInstallationStatusLabelAndColor(status) {
  switch (status) {
    case 'no_status':
      return ['No Status', 'grey'];
    case 'in_progress':
      return ['Installation in Progress', 'dark-yellow'];
    case 'completed':
      return ['Installation Complete', 'green'];
    case 'error':
      return ['Error', 'red'];
    default:
      return [status, 'white'];
  }
}

function getFormattedUpdateAt(date) {
  date = moment(date || undefined); // undefined moment returns current date
  return date.tz(moment.tz.guess()).format('MMMM Do YYYY, h:mm a z');
}
